import React from "react";
import styled from "styled-components";
import { FormattedMessage, useIntl } from "react-intl";
import { DateRange, MetricRange } from "../domain/experiment.entity";
import { ExperimentRepository } from "../domain/interfaces/ExperimentRepository";
import { useExperimentResults } from "../application/hooks/useExperimentResults";
import { messages } from "../experiment.messages";

export interface ExperimentHealthStripProps {
  propertyTicker: string;
  experimentId: string;
  // Read-only props from ExperimentResultsPage (US-017), which owns this shared state — this
  // component does not own or default it.
  range: MetricRange;
  custom: DateRange | undefined;
  repository: ExperimentRepository;
}

const OK_COLOR = "#1c7430";
const DRIFT_COLOR = "#8a5a00";

/** Four equal tiles on one row, two by two once the row can no longer hold them. */
const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;

  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Tile = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.buttonLightBorder};
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
  min-width: 0;
`;

const TileTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.regularTextColor};
`;

const TileValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
`;

const TileSub = styled.div`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.regularTextColor};
`;

const StatusText = styled.span<{ $ok: boolean }>`
  color: ${({ $ok }) => ($ok ? OK_COLOR : DRIFT_COLOR)};
  font-size: 0.72rem;
  font-weight: 600;
`;

function HealthTile({
  title,
  value,
  sub,
  testId,
}: {
  title: React.ReactNode;
  value: React.ReactNode;
  sub: React.ReactNode;
  testId: string;
}) {
  return (
    <Tile data-testid={testId}>
      <TileTitle>{title}</TileTitle>
      <TileValue>{value}</TileValue>
      <TileSub>{sub}</TileSub>
    </Tile>
  );
}

/**
 * The four "is this test healthy?" readings, none of which is a verdict: whether traffic split the
 * way it was configured, how many visitors actually count, how fast they are arriving, and how far
 * through the minimum duration the test is.
 *
 * Pure formatting of the shared results payload — it opens no query of its own beyond the one
 * React Query already de-duplicates across every section of this screen.
 */
export function ExperimentHealthStrip({
  propertyTicker,
  experimentId,
  range,
  custom,
  repository,
}: ExperimentHealthStripProps) {
  const intl = useIntl();
  const { data, isLoading, isError } = useExperimentResults(
    propertyTicker,
    experimentId,
    range,
    repository,
    custom
  );

  // Same discipline as the decision panel: no skeleton, no error tile — the sections around it
  // already speak for the shared query's loading and error states.
  if (isLoading || isError || !data) {
    return null;
  }

  const {
    scenarioA,
    scenarioB,
    observedShareA,
    srmAlert,
    activeDays,
    minDays,
    estimatedDaysLeft,
    hasEstimatedDaysLeft,
  } = data;

  const observedA = observedShareA * 100;
  const validVisitors = scenarioA.validVisitors + scenarioB.validVisitors;
  const excludedVisitors = scenarioA.excludedVisitors + scenarioB.excludedVisitors;
  // Visitors per day of actual exposure. `activeDays === 0` (the test started today) has no rate
  // to report yet — and the raw total is NOT a per-day rate: reporting it as one overstated the
  // pace by the whole run. The tile says "—" instead, like its own unknown-estimate caption.
  const hasPace = activeDays > 0;
  const pace = hasPace ? validVisitors / activeDays : 0;
  const hasMinDays = minDays > 0;
  const daysToMinimum = Math.max(minDays - activeDays, 0);

  return (
    <Strip data-testid="experiment-health-strip">
      <HealthTile
        testId="health-observed-split"
        title={<FormattedMessage {...messages.results.health.observedSplitTitle} />}
        value={
          <FormattedMessage
            {...messages.results.health.observedSplitValue}
            values={{
              a: intl.formatNumber(observedA, { maximumFractionDigits: 1 }),
              b: intl.formatNumber(100 - observedA, { maximumFractionDigits: 1 }),
            }}
          />
        }
        sub={
          // The configured split is fixed at 50/50 and no longer shown, so the SRM verdict is all
          // this caption carries. The tick is decorative: "OK"/"Desvío" carries the meaning in text.
          <StatusText $ok={!srmAlert} data-testid="health-split-status">
            {!srmAlert && <span aria-hidden="true">✓ </span>}
            <FormattedMessage
              {...(srmAlert
                ? messages.results.health.observedSplitDrift
                : messages.results.health.observedSplitOk)}
            />
          </StatusText>
        }
      />

      <HealthTile
        testId="health-valid-visitors"
        title={<FormattedMessage {...messages.results.health.visitorsTitle} />}
        value={intl.formatNumber(validVisitors)}
        sub={
          excludedVisitors > 0 ? (
            <FormattedMessage
              {...messages.results.health.visitorsSub}
              values={{ count: intl.formatNumber(excludedVisitors) }}
            />
          ) : (
            <FormattedMessage {...messages.results.health.visitorsSubNone} />
          )
        }
      />

      <HealthTile
        testId="health-pace"
        title={<FormattedMessage {...messages.results.health.paceTitle} />}
        value={
          hasPace ? (
            <FormattedMessage
              {...messages.results.health.paceValue}
              values={{ visitors: intl.formatNumber(pace, { maximumFractionDigits: 0 }) }}
            />
          ) : (
            "—"
          )
        }
        sub={
          // Only the backend's own estimate, never one re-derived from the pace above it.
          hasEstimatedDaysLeft ? (
            <FormattedMessage
              {...messages.results.health.paceSub}
              values={{ days: intl.formatNumber(estimatedDaysLeft) }}
            />
          ) : (
            <FormattedMessage {...messages.results.health.paceSubUnknown} />
          )
        }
      />

      <HealthTile
        testId="health-duration"
        title={<FormattedMessage {...messages.results.health.durationTitle} />}
        value={
          hasMinDays ? (
            <FormattedMessage
              {...messages.results.health.durationValue}
              values={{ activeDays, minDays }}
            />
          ) : (
            <FormattedMessage
              {...messages.results.health.durationValueNoMin}
              values={{ activeDays }}
            />
          )
        }
        sub={
          // No minimum configured means no minimum to be pending on OR to have covered: the tile
          // says nothing rather than claiming a condition was met.
          !hasMinDays ? null : daysToMinimum > 0 ? (
            <FormattedMessage
              {...messages.results.health.durationSubPending}
              values={{ days: daysToMinimum }}
            />
          ) : (
            <FormattedMessage {...messages.results.health.durationSubMet} />
          )
        }
      />
    </Strip>
  );
}
